import { useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Pencil, Trash2, Cpu, Loader2 } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABELS = { admin: "Administrador", editor: "Editor", viewer: "Visualizador" };
const ROLE_COLOR = {
  admin: "bg-[#DC2626] text-white",
  editor: "bg-secondary text-secondary-foreground",
  viewer: "bg-zinc-200 text-zinc-700",
};

export default function Users() {
  const { user } = useAuth();
  const isSuper = user.role === "superadmin";
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "viewer", company_id: "", equipment_numbers: [], client_code: "" });
  const [delTarget, setDelTarget] = useState(null);
  const [equipments, setEquipments] = useState([]);
  const [equipLoading, setEquipLoading] = useState(false);
  const [equipSearch, setEquipSearch] = useState("");

  const load = useCallback(() => api.get("/users").then((r) => setUsers(r.data)).catch(() => {}), []);
  useEffect(() => {
    load();
    if (isSuper) api.get("/companies-options").then((r) => setCompanies(r.data)).catch(() => {});
  }, [isSuper, load]);

  // Fetch G4S equipment catalog for the selected company (empty for non-G4S companies)
  useEffect(() => {
    if (!open || !form.company_id) {
      setEquipments([]);
      return;
    }
    setEquipLoading(true);
    api
      .get(`/g4s/equipments?company_id=${form.company_id}`)
      .then((r) => setEquipments(r.data))
      .catch(() => setEquipments([]))
      .finally(() => setEquipLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form.company_id]);

  const filteredEquipments = useMemo(() => {
    const q = equipSearch.toLowerCase();
    if (!q) return equipments;
    return equipments.filter(
      (e) => String(e.equipmentNumber).includes(q) || (e.tag || "").toLowerCase().includes(q)
    );
  }, [equipments, equipSearch]);

  const toggleEquip = (num) =>
    setForm((f) => ({
      ...f,
      equipment_numbers: f.equipment_numbers.includes(num)
        ? f.equipment_numbers.filter((n) => n !== num)
        : [...f.equipment_numbers, num],
    }));

  const roleOptions = isSuper ? ["admin", "editor", "viewer"] : ["editor", "viewer"];

  const openNew = () => {
    setEditing(null);
    setEquipSearch("");
    setForm({ name: "", email: "", password: "", role: "viewer", company_id: isSuper ? "" : user.company_id, equipment_numbers: [], client_code: "" });
    setOpen(true);
  };
  const openEdit = (u) => {
    setEditing(u);
    setEquipSearch("");
    setForm({ name: u.name, email: u.email, password: "", role: u.role, company_id: u.company_id || "", equipment_numbers: u.equipment_numbers || [], client_code: u.client_code || "" });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role, equipment_numbers: form.equipment_numbers, client_code: form.client_code };
        if (form.password) payload.password = form.password;
        if (isSuper) payload.company_id = form.company_id;
        await api.put(`/users/${editing.id}`, payload);
      } else {
        await api.post("/users", form);
      }
      toast.success(editing ? "Usuario actualizado" : "Usuario creado");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/users/${delTarget.id}`);
      toast.success("Usuario eliminado");
      setDelTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="w-full max-w-[1600px] mx-auto p-4 sm:p-6 lg:p-8">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Usuarios</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {isSuper ? "Gestiona administradores y usuarios de todas las empresas." : "Gestiona los usuarios de visualización y edición."}
          </p>
        </div>
        <Button className="rounded-sm gap-2 bg-[#DC2626] hover:bg-[#DC2626]/90" onClick={openNew} data-testid="add-user-button">
          <Plus className="h-4 w-4" /> Nuevo usuario
        </Button>
      </div>

      <div className="bg-card border border-border rounded-sm overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm" data-testid="users-table">
          <thead>
            <tr className="bg-muted border-b border-border">
              <th className="px-4 py-3 text-left label-caps">Nombre</th>
              <th className="px-4 py-3 text-left label-caps">Correo</th>
              <th className="px-4 py-3 text-left label-caps">Rol</th>
              {isSuper && <th className="px-4 py-3 text-left label-caps">Empresa</th>}
              <th className="px-4 py-3 text-left label-caps">Equipos</th>
              <th className="px-4 py-3 text-left label-caps">Cliente fijo</th>
              <th className="px-4 py-3 text-right label-caps">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-border" data-testid={`user-row-${u.email}`}>
                <td className="px-4 py-3 font-medium">{u.name}</td>
                <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                <td className="px-4 py-3">
                  <Badge className={`rounded-sm font-medium ${ROLE_COLOR[u.role]}`}>{ROLE_LABELS[u.role]}</Badge>
                </td>
                {isSuper && <td className="px-4 py-3 text-muted-foreground">{u.company_name}</td>}
                <td className="px-4 py-3 text-muted-foreground" data-testid={`user-equipments-${u.email}`}>
                  {u.equipment_numbers && u.equipment_numbers.length > 0
                    ? `${u.equipment_numbers.length} equipo(s)`
                    : <span className="text-muted-foreground">Todos / —</span>}
                </td>
                <td className="px-4 py-3" data-testid={`user-client-${u.email}`}>
                  {u.client_code ? <Badge className="rounded-sm bg-[#DC2626] text-white font-medium">{u.client_code}</Badge> : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="px-4 py-3 text-right">
                  <Button size="icon" variant="ghost" className="h-8 w-8 rounded-sm" onClick={() => openEdit(u)} data-testid={`edit-user-${u.email}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8 rounded-sm text-[#E11D48]" onClick={() => setDelTarget(u)} data-testid={`delete-user-${u.email}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </td>
              </tr>
            ))}
            {!users.length && (
              <tr><td colSpan={isSuper ? 7 : 6} className="py-12 text-center text-muted-foreground">No hay usuarios registrados.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Editar usuario" : "Nuevo usuario"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="label-caps">Nombre</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" data-testid="user-name-input" />
            </div>
            <div className="space-y-2">
              <Label className="label-caps">Correo</Label>
              <Input type="email" value={form.email} disabled={!!editing} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-sm" data-testid="user-email-input" />
            </div>
            <div className="space-y-2">
              <Label className="label-caps">{editing ? "Nueva contraseña (opcional)" : "Contraseña"}</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-sm" data-testid="user-password-input" />
            </div>
            <div className="space-y-2">
              <Label className="label-caps">Número de cliente (filtro fijo, opcional)</Label>
              <Input value={form.client_code} onChange={(e) => setForm({ ...form, client_code: e.target.value })} placeholder="Ej. 1234" className="rounded-sm" data-testid="user-client-code-input" />
              <p className="text-xs text-muted-foreground">Si lo defines, este usuario solo verá consultas de ese número de cliente.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="label-caps">Rol</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {roleOptions.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {isSuper && (
                <div className="space-y-2">
                  <Label className="label-caps">Empresa</Label>
                  <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="user-company-select"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                    <SelectContent>
                      {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            {form.company_id && (equipLoading || equipments.length > 0) && (
              <div className="space-y-2" data-testid="equipment-assign">
                <Label className="label-caps flex items-center gap-1"><Cpu className="h-3.5 w-3.5" /> Equipos Gas-PAR con acceso ({form.equipment_numbers.length})</Label>
                {equipLoading ? (
                  <div className="text-sm text-muted-foreground flex items-center gap-2 py-3"><Loader2 className="h-4 w-4 animate-spin" /> Cargando catálogo de equipos…</div>
                ) : (
                  <div className="border border-border rounded-sm">
                    <Input
                      value={equipSearch}
                      onChange={(e) => setEquipSearch(e.target.value)}
                      placeholder="Buscar por número o etiqueta…"
                      className="rounded-none border-0 border-b border-border h-9"
                      data-testid="equipment-search"
                    />
                    <div className="max-h-48 overflow-y-auto p-1">
                      {filteredEquipments.map((e) => (
                          <label key={e.equipmentNumber} className="flex items-center gap-2 px-2 py-1.5 hover:bg-muted cursor-pointer text-sm rounded-sm" data-testid={`equipment-option-${e.equipmentNumber}`}>
                            <Checkbox checked={form.equipment_numbers.includes(e.equipmentNumber)} onCheckedChange={() => toggleEquip(e.equipmentNumber)} />
                            <span className="font-medium tabular-nums w-12">#{e.equipmentNumber}</span>
                            <span className="flex-1 truncate">{e.tag}</span>
                            <span className="text-xs text-muted-foreground">{e.applicationType}</span>
                          </label>
                        ))}
                    </div>
                    <div className="flex justify-between items-center px-2 py-1.5 border-t border-border text-xs">
                      <button type="button" className="text-[#DC2626]" onClick={() => setForm((f) => ({ ...f, equipment_numbers: equipments.map((x) => x.equipmentNumber) }))} data-testid="equipment-select-all">Seleccionar todos</button>
                      <button type="button" className="text-muted-foreground" onClick={() => setForm((f) => ({ ...f, equipment_numbers: [] }))} data-testid="equipment-clear">Limpiar</button>
                    </div>
                  </div>
                )}
                <p className="text-xs text-muted-foreground">Define a qué equipos Gas-PAR tendrá acceso este usuario en la Consulta.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button className="rounded-sm bg-[#DC2626] hover:bg-[#DC2626]/90" onClick={save} data-testid="save-user-button">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar usuario?</AlertDialogTitle>
            <AlertDialogDescription>Se eliminará a "{delTarget?.name}". Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancelar</AlertDialogCancel>
            <AlertDialogAction className="rounded-sm bg-[#E11D48] hover:bg-[#E11D48]/90" onClick={remove} data-testid="confirm-delete-user">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
